import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, PackageSearch, CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const since = (iso, t) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return t("justNow");
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)} u`;
  return `${Math.floor(m / 1440)} d`;
};

/* Reception / warehouse bell: mechanic parts requests + cars ready. Polls every 20s. */
export default function NotificationBell() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), enabled: !!user, refetchInterval: 20000 });
  const items = data?.items || [];
  const unread = data?.unread || 0;
  if (!data || (items.length === 0 && !unread)) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const openItem = async (n) => {
    await api.post("/notifications/read", [n.id]);
    setOpen(false); refresh();
    if (n.repair_id) nav(`/repairs?card=${n.repair_id}`);
  };
  const resolve = async (e, n) => { e.stopPropagation(); await api.post(`/notifications/${n.id}/resolve`); refresh(); };
  const readAll = async () => { await api.post("/notifications/read"); refresh(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 grid place-items-center rounded-full hover:bg-accent" data-testid="notification-bell" aria-label={t("notifications")}>
          <Bell className={`h-5 w-5 ${unread ? "text-primary" : "text-muted-foreground"}`} />
          {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center" data-testid="notification-count">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-semibold text-sm">{t("notifications")}</div>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={readAll} data-testid="notification-read-all"><CheckCheck className="h-3.5 w-3.5 mr-1" />{t("markAllRead")}</Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
          {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("noNotifications")}</div>}
          {items.map((n) => (
            <div key={n.id} onClick={() => openItem(n)} className={`flex gap-3 px-4 py-3 cursor-pointer hover:bg-accent/60 ${n.resolved ? "opacity-50" : n.read ? "" : "bg-primary/5"}`} data-testid={`notification-item-${n.id}`}>
              <div className={`h-9 w-9 rounded-full grid place-items-center shrink-0 ${n.kind === "parts_request" ? (n.meta?.urgent ? "bg-rose-500/15 text-rose-500" : "bg-orange-500/15 text-orange-500") : "bg-emerald-500/15 text-emerald-500"}`}>
                {n.kind === "parts_request" ? <PackageSearch className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm leading-snug ${n.read ? "" : "font-semibold"}`}>{n.title}</div>
                <div className="text-xs text-muted-foreground truncate">{n.body}</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{since(n.created_at, t)} · {n.by}</div>
              </div>
              {!n.resolved && <button onClick={(e) => resolve(e, n)} className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-accent shrink-0" title={t("markHandled")} data-testid={`notification-resolve-${n.id}`}><X className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
